import React from "react";

import {
  AddCircleOutlineRounded,
  DataObjectRounded,
  DnsRounded,
  FileDownloadRounded,
  FileUploadRounded,
  MenuRounded,
} from "@mui/icons-material";
import { Box } from "@mui/material";
import { httpsCallable } from "firebase/functions";
import { useNavigate } from "react-router-dom";

import useFirebase from "../../../config/context/firebaseContext";
import useMainState from "../../../config/context/mainStateContext";
import useModal from "../../../config/context/modalContext";
import { ExportService } from "../../../config/services/ExportService";
import { Dev } from "../../../config/util/consoleUtil";

import AutocompleteMain from "../../../components/InputMain/AutocompleteMain";
import TextMain from "../../../components/InputMain/TextMain";
import MainSolidButton from "../../../components/MainSolidButton";
import MainText from "../../../components/MainText";
import PageHeader from "../../../components/PageHeader";

export default function AdminTestPage() {
  const navigate = useNavigate();

  const { pushAlert } = useModal();
  const { setShowSideMenuDrawer } = useMainState();
  const { getConfig, functions } = useFirebase();

  const DownloadDocs = ExportService.All();
  const MoveDocs = ExportService.Move();

  const [collectionField, setCollectionField] = React.useState("");

  const [customDataField, setCustomDataField] = React.useState("");
  const [customCollectionField, setCustomCollectionField] = React.useState("");
  const [isValidJson, setIsValidJson] = React.useState(false);
  const [validJson, setValidJson] = React.useState([]);

  const [exportationLoading, setExportationLoading] = React.useState(false);

  const [exportItems, setExportItems] = React.useState<any[] | false>(false);
  const [exportSubItems, setExportSubItems] = React.useState<any[] | false>(false);

  const COLLECTIONS = [
    "authCodes",
    "cafeterias",
    "chats",
    "files",
    "menu",
    "notificationLogs",
    "requestLogs",
    "errorLogs",
    "spotts",
    "topics",
    "topicSubscriptions",
    "users",
  ];

  const exportViaFunctions = async () => {
    try {
      setExportationLoading(true);
      const exportationFetch = httpsCallable(functions, "exportCollection");

      const payload = { collection: collectionField };

      const response = await exportationFetch(payload);

      pushAlert({ message: "Documentos baixados", type: "success" });
      console.log(...new Dev("handleGenerateExportation", response.data).Pink());

      setExportItems((response as any)?.data?.items || []);
      setExportSubItems((response as any)?.data?.withSubCollection || []);
    } catch (error) {
      console.log(...new Dev("handleGenerateExportation", error).Error());
      pushAlert({
        type: "error",
        message: "Erro ao obter exportação",
      });
    } finally {
      setExportationLoading(false);
    }
  };

  const exportViaListing = async () => {
    const res = await DownloadDocs.runRequest(collectionField);
    pushAlert({ message: "Documentos baixados", type: "success" });
    console.log(...new Dev("Download Docs Response", res).Pink());
    setExportItems((res as any[]) || []);
    setExportSubItems(false);
  };

  const handleExport = (exportType: "list" | "func") => {
    if (collectionField)
      pushAlert({
        type: "warning",
        message: `Baixar documentos de '${collectionField}'`,
        isConfirm: true,
        onConfirm: async () => {
          if (exportType === "list") exportViaListing();
          else exportViaFunctions();
        },
      });
    else pushAlert({ message: "Preencha o campo de Coleção", type: "error" });
  };

  const handleMove = async (collectionToPopulate: string, itemsToPopulate?: any[]) => {
    if (collectionToPopulate) {
      if (itemsToPopulate) {
        pushAlert({
          type: "warning",
          message: `Criar documentos em '${collectionToPopulate}'`,
          isConfirm: true,
          onConfirm: async () => {
            const res = await MoveDocs.runRequest(collectionToPopulate, itemsToPopulate);
            pushAlert({ message: "Documentos movidos", type: "success" });
            console.log(...new Dev("Download Docs Response", res).Pink());
          },
        });
      } else pushAlert({ message: "Nenhum documento para mover", type: "error" });
    } else pushAlert({ message: "Preencha o campo de Coleção", type: "error" });
  };

  const handleValidateJSON = async () => {
    try {
      const validatedJson = await JSON.parse(customDataField);
      if (!("length" in validatedJson)) {
        pushAlert({
          type: "error",
          message: "JSON Precisa ser uma lista",
        });
        throw Error("JSON Valid, but Array format is required.");
      }
      setValidJson(validatedJson);
      setIsValidJson(true);
      pushAlert({
        type: "success",
        message: "JSON Válido",
      });
    } catch (error) {
      console.log(...new Dev("handleGenerateExportation", error).Error());
      setIsValidJson(false);
      pushAlert({
        type: "error",
        message: "JSON Inválido",
      });
    }
  };

  return (
    <Box
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
      }}
      pt={"30px"}
      pb={"45px"}
    >
      <Box width={"90%"}>
        <PageHeader
          leftIcon={<MenuRounded />}
          onLeftAction={() => setShowSideMenuDrawer(true)}
          title="Testes"
        />

        {/* Collection Export */}

        <Box mb={2}>
          <MainText size="body">Exportar Coleção</MainText>
        </Box>

        <AutocompleteMain
          name="Coleção"
          value={collectionField}
          options={COLLECTIONS}
          onChange={setCollectionField}
        />

        <Box mb={2}>
          <MainSolidButton
            icon={<DnsRounded />}
            text="Baixar Via Function"
            loading={exportationLoading}
            onClick={() => handleExport("func")}
          />
        </Box>

        <Box mb={2}>
          <MainSolidButton
            icon={<FileDownloadRounded />}
            text="Baixar Via Listagem"
            loading={DownloadDocs.isLoading}
            onClick={() => handleExport("list")}
          />
        </Box>

        {exportItems !== false && !DownloadDocs.isLoading && !exportationLoading && (
          <Box mb={2}>
            <MainText size="small" color="regular">
              Documentos: {exportItems.length}
            </MainText>
            <MainText size="small" color="regular">
              {exportSubItems !== false
                ? `Documentos com Sub Coleções: ${exportSubItems.length}`
                : "Sub Coleções não exibidas nessa listagem"}
            </MainText>
          </Box>
        )}

        <Box mb={2}>
          <MainSolidButton
            icon={<FileUploadRounded />}
            text="Mover Docs"
            loading={MoveDocs.isLoading}
            onClick={() => handleMove(collectionField, exportItems || undefined)}
          />
          <Box mt={1}>
            <MainText size="small" color="regular">
              OBS: Só move os campos do documento, não move as Sub Coleções
            </MainText>
          </Box>
        </Box>

        <Box mt={4}>
          <TextMain
            lines={5}
            borderColor={"regular"}
            textColor={"regular"}
            inputType="text"
            name="JSON"
            onChange={(value) => {
              setCustomDataField(value);
              setIsValidJson(false);
            }}
            value={customDataField}
          />
          <TextMain
            borderColor={"regular"}
            textColor={"regular"}
            inputType="text"
            name="Coleção"
            onChange={setCustomCollectionField}
            value={customCollectionField}
          />

          {customDataField.length > 0 && (
            <Box mb={2}>
              <MainText size="small" color={isValidJson ? "black" : "danger"}>
                {isValidJson ? "JSON Válido" : "JSON Inválido"}
              </MainText>
            </Box>
          )}
        </Box>

        <Box mb={2}>
          {isValidJson ? (
            <MainSolidButton
              icon={<AddCircleOutlineRounded />}
              text="Criar Coleção"
              loading={MoveDocs.isLoading}
              onClick={() => handleMove(customCollectionField, validJson)}
            />
          ) : (
            <MainSolidButton
              icon={<DataObjectRounded />}
              text="Validar JSON"
              onClick={handleValidateJSON}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
